import React, { useState } from 'react';
import MapLocationPicker from './ui/MapLocationPicker';
import { Plus, Trash2, X } from 'lucide-react';
import { computeAge } from '../utils/validationRules';

const TODAY_ISO = new Date().toISOString().split('T')[0];

export default function OnboardingWizard({ initialData, onSubmit, onCancel, isGoogleMode }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    first_name: initialData?.first_name || '',
    last_name: initialData?.last_name || '',
    email: initialData?.email || '',
    password: '',
    phone_number: '',
    address: '',
    entity_type: 'Individual',
    company_name: '',
    date_of_birth: '',
    gender: 'Male',
    farms: [
      {
        farm_name: '',
        area: '',
        latitude: null,
        longitude: null,
        crops: [{ crop_name: '', planted_area: '', is_tree: false, trees: [] }]
      }
    ]
  });

  const handleAddFarm = () => {
    setFormData(prev => ({
      ...prev,
      farms: [...prev.farms, { farm_name: '', area: '', latitude: null, longitude: null, crops: [{ crop_name: '', planted_area: '', is_tree: false, trees: [] }] }]
    }));
  };

  const handleRemoveFarm = (index) => {
    setFormData(prev => {
      const updatedFarms = [...prev.farms];
      updatedFarms.splice(index, 1);
      return { ...prev, farms: updatedFarms };
    });
  };

  const handleFarmChange = (index, field, value) => {
    setFormData(prev => {
      const updatedFarms = [...prev.farms];
      updatedFarms[index] = { ...updatedFarms[index], [field]: value };
      return { ...prev, farms: updatedFarms };
    });
  };

  const handleAddCrop = (farmIndex) => {
    setFormData(prev => {
      const updatedFarms = [...prev.farms];
      updatedFarms[farmIndex].crops.push({ crop_name: '', planted_area: '', is_tree: false, trees: [] });
      return { ...prev, farms: updatedFarms };
    });
  };

  const handleRemoveCrop = (farmIndex, cropIndex) => {
    setFormData(prev => {
      const updatedFarms = [...prev.farms];
      updatedFarms[farmIndex].crops.splice(cropIndex, 1);
      return { ...prev, farms: updatedFarms };
    });
  };

  const handleCropChange = (farmIndex, cropIndex, field, value) => {
    setFormData(prev => {
      const updatedFarms = [...prev.farms];
      updatedFarms[farmIndex].crops[cropIndex] = { ...updatedFarms[farmIndex].crops[cropIndex], [field]: value };
      return { ...prev, farms: updatedFarms };
    });
  };

  const handleAddTree = (farmIndex, cropIndex) => {
    setFormData(prev => {
      const updatedFarms = [...prev.farms];
      updatedFarms[farmIndex].crops[cropIndex].trees.push({ tree_name: '', tree_code: '' });
      return { ...prev, farms: updatedFarms };
    });
  };

  const handleRemoveTree = (farmIndex, cropIndex, treeIndex) => {
    setFormData(prev => {
      const updatedFarms = [...prev.farms];
      updatedFarms[farmIndex].crops[cropIndex].trees.splice(treeIndex, 1);
      return { ...prev, farms: updatedFarms };
    });
  };

  const handleTreeChange = (farmIndex, cropIndex, treeIndex, field, value) => {
    setFormData(prev => {
      const updatedFarms = [...prev.farms];
      updatedFarms[farmIndex].crops[cropIndex].trees[treeIndex] = { 
        ...updatedFarms[farmIndex].crops[cropIndex].trees[treeIndex], 
        [field]: value 
      };
      return { ...prev, farms: updatedFarms };
    });
  };

  const nextStep = () => {
    const form = document.getElementById("onboarding-form");
    if (form && !form.checkValidity()) {
        form.reportValidity();
        return;
    }
    
    if (step === 2 && formData.entity_type === 'Company' && !formData.company_name) {
        return;
    }
    setStep(prev => Math.min(prev + 1, 3));
  };
  
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = JSON.parse(JSON.stringify(formData));
    
    // Clean up empty farms and format crops
    payload.farms = payload.farms.filter(f => f.farm_name.trim() !== '' || f.latitude !== null);
    
    payload.farms = payload.farms.map(f => {
      f.area = f.area ? parseFloat(f.area) : 0.0;
      f.crops = f.crops.filter(c => c.crop_name.trim() !== '').map(c => ({
        crop_name: c.crop_name.trim(),
        planted_area: c.planted_area ? parseFloat(c.planted_area) : 0.0,
        is_tree: !!c.is_tree,
        trees: c.is_tree ? (c.trees || []).filter(t => t.tree_name.trim() !== '').map(t => ({
          tree_name: t.tree_name.trim(),
          tree_code: t.tree_code.trim()
        })) : []
      }));
      return f;
    });

    if (payload.farms.length === 0) payload.farms = null;

    onSubmit(payload);
  };

  return (
    <div className="w-full max-w-2xl mx-auto p-6 bg-white/95 dark:bg-slate-800/95 rounded-2xl shadow-xl backdrop-blur-sm border border-gray-100 dark:border-slate-700 max-h-[90vh] overflow-y-auto custom-scrollbar">
      <h2 className="text-2xl font-bold text-center mb-6 text-[#1B263B] dark:text-white">
        {isGoogleMode ? "Complete Your Profile" : "Create Account"}
      </h2>
      <div className="flex justify-between mb-8">
        {[1, 2, 3].map(i => (
          <div key={i} className={`flex-1 h-2 mx-1 rounded-full transition-colors duration-300 ${step >= i ? 'bg-geo-copper' : 'bg-gray-200 dark:bg-slate-600'}`}></div>
        ))}
      </div>

      <form id="onboarding-form" onSubmit={(e) => e.preventDefault()} className="space-y-5">
        {step === 1 && (
          <div className="animate-fade-in-slide space-y-4 text-[#1B263B] dark:text-white">
            <div className="flex gap-4">
              <div className="w-1/2">
                <label className="block text-sm font-medium mb-1">First Name *</label>
                <input type="text" required value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} className="w-full p-3 rounded-lg border border-gray-200 dark:bg-slate-700 dark:border-slate-600 focus:border-geo-copper outline-none transition-colors" />
              </div>
              <div className="w-1/2">
                <label className="block text-sm font-medium mb-1">Last Name *</label>
                <input type="text" required value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} className="w-full p-3 rounded-lg border border-gray-200 dark:bg-slate-700 dark:border-slate-600 focus:border-geo-copper outline-none transition-colors" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email Address *</label>
              <input type="email" required readOnly={isGoogleMode} value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className={`w-full p-3 rounded-lg border border-gray-200 dark:bg-slate-700 dark:border-slate-600 focus:border-geo-copper outline-none transition-colors ${isGoogleMode ? 'opacity-60 cursor-not-allowed bg-gray-50 dark:bg-slate-800' : ''}`} />
            </div>
            {!isGoogleMode && (
              <div>
                <label className="block text-sm font-medium mb-1">Create Password *</label>
                <input type="password" required value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} className="w-full p-3 rounded-lg border border-gray-200 dark:bg-slate-700 dark:border-slate-600 focus:border-geo-copper outline-none transition-colors" />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Phone Number *</label>
              <input type="text" required value={formData.phone_number} onChange={e => setFormData({...formData, phone_number: e.target.value})} className="w-full p-3 rounded-lg border border-gray-200 dark:bg-slate-700 dark:border-slate-600 focus:border-geo-copper outline-none transition-colors" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Address *</label>
              <input type="text" required value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} className="w-full p-3 rounded-lg border border-gray-200 dark:bg-slate-700 dark:border-slate-600 focus:border-geo-copper outline-none transition-colors" />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade-in-slide space-y-4 text-[#1B263B] dark:text-white">
            <div>
              <label className="block text-sm font-medium mb-1">Entity Type *</label>
              <select value={formData.entity_type} onChange={e => setFormData({...formData, entity_type: e.target.value})} className="w-full p-3 rounded-lg border border-gray-200 dark:bg-slate-700 dark:border-slate-600 focus:border-geo-copper outline-none transition-colors">
                <option value="Individual">Individual</option>
                <option value="Company">Company</option>
              </select>
            </div>
            {formData.entity_type === 'Company' && (
              <div className="animate-fade-in-slide">
                <label className="block text-sm font-medium mb-1">Company Name *</label>
                <input type="text" required value={formData.company_name} onChange={e => setFormData({...formData, company_name: e.target.value})} className="w-full p-3 rounded-lg border border-gray-200 dark:bg-slate-700 dark:border-slate-600 focus:border-geo-copper outline-none transition-colors" />
              </div>
            )}
            <div className="flex gap-4">
              <div className="w-1/2">
                <label className="block text-sm font-medium mb-1">Date of Birth *</label>
                <input type="date" required min="1900-01-01" max={TODAY_ISO} value={formData.date_of_birth} onChange={e => setFormData({...formData, date_of_birth: e.target.value})} className="w-full p-3 rounded-lg border border-gray-200 dark:bg-slate-700 dark:border-slate-600 focus:border-geo-copper outline-none transition-colors" />
                {formData.date_of_birth && (
                  <p className="text-xs text-geo-olive mt-1 font-medium">Age: {computeAge(formData.date_of_birth)} years</p>
                )}
              </div>
              <div className="w-1/2">
                <label className="block text-sm font-medium mb-1">Gender *</label>
                <select value={formData.gender} onChange={e => setFormData({...formData, gender: e.target.value})} className="w-full p-3 rounded-lg border border-gray-200 dark:bg-slate-700 dark:border-slate-600 focus:border-geo-copper outline-none transition-colors">
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="animate-fade-in-slide space-y-6 text-[#1B263B] dark:text-white pb-6">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Farms & Locations (Optional)</p>
            
            {formData.farms.map((farm, farmIndex) => (
              <div key={farmIndex} className="p-4 border border-gray-200 dark:border-slate-600 rounded-xl relative bg-gray-50/50 dark:bg-slate-800/50 shadow-sm">
                {formData.farms.length > 1 && (
                  <button type="button" onClick={() => handleRemoveFarm(farmIndex)} className="absolute top-4 right-4 text-rose-500 hover:text-rose-700 p-1 bg-white dark:bg-slate-700 rounded-full shadow-sm">
                    <Trash2 size={18} />
                  </button>
                )}
                <h3 className="font-semibold mb-4 text-[#C47A2C]">Farm {farmIndex + 1}</h3>
                
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="w-2/3">
                      <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-300">Farm Name</label>
                      <input type="text" value={farm.farm_name} onChange={e => handleFarmChange(farmIndex, 'farm_name', e.target.value)} className="w-full p-2.5 rounded-lg border border-gray-200 dark:bg-slate-700 dark:border-slate-600 focus:border-geo-copper outline-none text-sm" />
                    </div>
                    <div className="w-1/3">
                      <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-300">Total Area (Ha)</label>
                      <input type="number" step="any" value={farm.area} onChange={e => handleFarmChange(farmIndex, 'area', e.target.value)} className="w-full p-2.5 rounded-lg border border-gray-200 dark:bg-slate-700 dark:border-slate-600 focus:border-geo-copper outline-none text-sm" />
                    </div>
                  </div>

                  {/* Dynamic Crops */}
                  <div className="bg-white dark:bg-slate-900/50 p-3 rounded-lg border border-gray-100 dark:border-slate-700 shadow-sm">
                    <label className="block text-xs font-medium mb-2 text-gray-600 dark:text-gray-300">Crops Planted</label>
                    {farm.crops.map((crop, cropIndex) => (
                      <div key={cropIndex} className="mb-4 p-3 bg-gray-50/50 dark:bg-slate-800/30 rounded-lg border border-gray-100 dark:border-slate-700">
                        <div className="flex gap-2 mb-2 items-center">
                          <input type="text" placeholder="Crop Name (e.g. Wheat)" value={crop.crop_name} onChange={e => handleCropChange(farmIndex, cropIndex, 'crop_name', e.target.value)} className="flex-1 p-2 rounded-md border border-gray-200 dark:bg-slate-700 dark:border-slate-600 focus:border-geo-copper outline-none text-sm" />
                          <input type="number" step="any" placeholder="Area (Ha)" value={crop.planted_area} onChange={e => handleCropChange(farmIndex, cropIndex, 'planted_area', e.target.value)} className="w-24 p-2 rounded-md border border-gray-200 dark:bg-slate-700 dark:border-slate-600 focus:border-geo-copper outline-none text-sm" />
                          {farm.crops.length > 1 && (
                            <button type="button" onClick={() => handleRemoveCrop(farmIndex, cropIndex)} className="text-gray-400 hover:text-rose-500 p-1">
                              <X size={16} />
                            </button>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 mt-2 ml-1">
                          <input type="checkbox" id={`is_tree_${farmIndex}_${cropIndex}`} checked={crop.is_tree} onChange={e => handleCropChange(farmIndex, cropIndex, 'is_tree', e.target.checked)} className="w-4 h-4 text-geo-copper rounded border-gray-300 focus:ring-geo-copper" />
                          <label htmlFor={`is_tree_${farmIndex}_${cropIndex}`} className="text-xs text-gray-600 dark:text-gray-300">Is this a tree/palm crop?</label>
                        </div>

                        {crop.is_tree && (
                          <div className="mt-3 pl-4 border-l-2 border-geo-copper/30 space-y-2">
                            {(crop.trees || []).map((tree, treeIndex) => (
                              <div key={treeIndex} className="flex gap-2 items-center">
                                <input type="text" placeholder="Tree/Palm Name" value={tree.tree_name} onChange={e => handleTreeChange(farmIndex, cropIndex, treeIndex, 'tree_name', e.target.value)} className="flex-1 p-2 rounded-md border border-gray-200 dark:bg-slate-700 dark:border-slate-600 focus:border-geo-copper outline-none text-sm" />
                                <input type="text" placeholder="Tree Code" value={tree.tree_code} onChange={e => handleTreeChange(farmIndex, cropIndex, treeIndex, 'tree_code', e.target.value)} className="w-32 p-2 rounded-md border border-gray-200 dark:bg-slate-700 dark:border-slate-600 focus:border-geo-copper outline-none text-sm" />
                                <button type="button" onClick={() => handleRemoveTree(farmIndex, cropIndex, treeIndex)} className="text-gray-400 hover:text-rose-500 p-1">
                                  <X size={14} />
                                </button>
                              </div>
                            ))}
                            <button type="button" onClick={() => handleAddTree(farmIndex, cropIndex)} className="text-xs text-geo-copper font-medium flex items-center mt-1 hover:text-[#A36322]">
                              <Plus size={12} className="mr-1" /> Add Tree/Palm
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    <button type="button" onClick={() => handleAddCrop(farmIndex)} className="text-xs text-geo-olive font-medium flex items-center mt-2 hover:text-[#527056]">
                      <Plus size={14} className="mr-1" /> Add another crop
                    </button>
                  </div>

                  {/* Inline Map */}
                  <div>
                    <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-300">Farm Location</label>
                    <MapLocationPicker 
                      latitude={farm.latitude} 
                      longitude={farm.longitude} 
                      onChange={(lat, lng) => {
                        handleFarmChange(farmIndex, 'latitude', lat);
                        handleFarmChange(farmIndex, 'longitude', lng);
                      }} 
                    />
                  </div>
                </div>
              </div>
            ))}
            
            <button type="button" onClick={handleAddFarm} className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-slate-600 text-gray-500 dark:text-gray-400 font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors flex justify-center items-center">
              <Plus size={18} className="mr-2" /> Add Another Farm
            </button>
          </div>
        )}

        <div className="flex justify-between mt-4 pt-4 border-t border-gray-100 dark:border-slate-700 sticky bottom-0 bg-white/95 dark:bg-slate-800/95">
          {step > 1 ? (
            <button type="button" onClick={prevStep} className="px-5 py-2.5 text-gray-600 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors">Back</button>
          ) : (
            <button type="button" onClick={onCancel} className="px-5 py-2.5 text-gray-600 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors">Cancel</button>
          )}

          {step < 3 ? (
            <button type="button" onClick={nextStep} className="px-8 py-2.5 bg-geo-copper text-white font-bold rounded-lg shadow-md hover:bg-orange-700 transition-all transform hover:-translate-y-0.5">Next</button>
          ) : (
            <button type="button" onClick={handleSubmit} className="px-8 py-2.5 bg-geo-olive text-white font-bold rounded-lg shadow-md hover:bg-[#527056] transition-all transform hover:-translate-y-0.5">Complete Setup</button>
          )}
        </div>
      </form>
    </div>
  );
}
